import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { DocumentInbox } from '../../models/document.inbox.model';
import { Error } from '../../models/error.model';
import { MediaDA } from '../../services/admin/media.service';
import { QuoteDA } from '../../services/admin/quote.service';
import { OrderDA } from '../../services/admin/order.service';
import { InvoiceDA } from '../../services/admin/invoice.service';
import { ProviderOrderDA } from '../../services/admin/provider.order.service';
import { ProviderInvoiceDA } from '../../services/admin/provider.invoice.service';
import { ExpenseDA } from '../../services/admin/expense.service';
import { AccountingEntryDA } from '../../services/admin/accounting.entry.service';
import { EmployeeDA } from '../../services/admin/employee.service';
import { ContractDA } from '../../services/admin/contract.service';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { dateFormat } from '../../scripts/date.time.format.script';

import AppLoading from '../Loading';
import AppPdfViewer from '../element/PdfViewer';

export type VoucherEntityType = 'quote' | 'order' | 'invoice' | 'provider-order' | 'provider-invoice' | 'expense' | 'accounting-entry' | 'employee' | 'contract'

export interface AppModalAttachDocumentProps {
  id_company: string,
  entity_type: VoucherEntityType,
  id_entity?: string,
  onAttached: (updatedEntity: any) => void,
  onPick?: (id_document_inbox: string, doc?: { name: string }) => void,
  modalId?: string
};

let errorResponse: Error;

function attachVoucherByEntityType(entity_type: VoucherEntityType, id_company: string, id_entity: string, body: { id_document_inbox: string }) {
  switch (entity_type) {
    case 'quote':            return QuoteDA.attachVoucher(id_company, id_entity, body)
    case 'order':            return OrderDA.attachVoucher(id_company, id_entity, body)
    case 'invoice':          return InvoiceDA.attachVoucher(id_company, id_entity, body)
    case 'provider-order':   return ProviderOrderDA.attachVoucher(id_company, id_entity, body)
    case 'provider-invoice': return ProviderInvoiceDA.attachVoucher(id_company, id_entity, body)
    case 'expense':          return ExpenseDA.attachVoucher(id_company, id_entity, body)
    case 'accounting-entry': return AccountingEntryDA.attachVoucher(id_company, id_entity, body)
    case 'employee':         return EmployeeDA.attachVoucher(id_company, id_entity, body)
    case 'contract':         return ContractDA.attachVoucher(id_company, id_entity, body)
  }
}

const AppModalAttachDocument: React.FunctionComponent<AppModalAttachDocumentProps> = ({id_company, entity_type, id_entity, onAttached, onPick, modalId}) => {
  const MODAL_ID = modalId ?? 'modal-attach-document'
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [loading, setLoading] = useState(false)
  const [documents, setDocuments] = useState<DocumentInbox[]>([])
  const [selectedDocId, setSelectedDocId] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'new' | 'assigned' | 'all'>('new')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewDocId, setPreviewDocId] = useState<string | null>(null)

  const loadDocuments = async () => {
    if (!id_company) return
    setLoading(true)
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter
      const response = await MediaDA.getDocuments(id_company, status as any, 'dated_on', 'desc', 1, 100)
      if (response.status === 200) {
        setDocuments(response.data?.items ?? [])
      }
    } catch (_error) {
      // silent
    } finally {
      setLoading(false)
    }
  }

  const filteredDocuments = searchTerm.trim()
    ? documents.filter(doc => {
        const term = searchTerm.toLowerCase()
        return (doc.name ?? '').toLowerCase().includes(term)
          || (doc.original_filename ?? '').toLowerCase().includes(term)
      })
    : documents

  const handlePreview = async (docId: string) => {
    if (docId === previewDocId) return
    setPreviewDocId(docId)
    setSelectedDocId(docId)
    setPreviewLoading(true)
    setPreviewUrl(null)
    try {
      const url = await MediaDA.getDocumentSignedUrl(id_company, docId)
      if (url) {
        setPreviewUrl(url)
      }
    } catch (_e) {
      setPreviewUrl(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const closePreview = () => {
    setPreviewUrl(null)
    setPreviewDocId(null)
    setPreviewLoading(false)
  }

  const submitAttach = async () => {
    if (!id_company || !selectedDocId) return

    if (onPick) {
      const picked = documents.find(d => (d.id ?? (d as any)._id) === selectedDocId)
      onPick(selectedDocId, picked ? { name: picked.name ?? picked.original_filename ?? '' } : undefined)
      modalHide(MODAL_ID)
      setSelectedDocId('')
      return
    }

    if (!id_entity) return
    setLoadIndicator('on')

    try {
      const response = await attachVoucherByEntityType(entity_type, id_company, id_entity, { id_document_inbox: selectedDocId })

      if (response?.status === 200 || response?.status === 201) {
        const updated = response.data?.data ?? response.data ?? response
        modalHide(MODAL_ID)
        setSelectedDocId('')

        GlobalSwal.fire({
          title: lang.labels.documentAttached,
          text: lang.labels.actionCompletedReturningToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then( () => {
          onAttached(updated)
        })
      } else {
        errorResponse = response?.data

        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoadIndicator('off')
    }
  }

  useEffect( () => {
    const modalEl = document.getElementById(MODAL_ID)
    if (modalEl) {
      const handleShown = () => {
        closePreview()
        setSelectedDocId('')
        loadDocuments()
      }
      modalEl.addEventListener('shown.bs.modal', handleShown)
      return () => modalEl.removeEventListener('shown.bs.modal', handleShown)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_company, statusFilter])

  useEffect(() => {
    loadDocuments()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  return (
    <div id={MODAL_ID} className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className={`modal-dialog modal-dialog-centered ${previewDocId !== null ? 'modal-xl' : 'mw-600px'}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title">{lang.labels.attachExistingDocument}</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body py-5 px-lg-10">
            <div className="d-flex flex-column flex-lg-row">
              <div className={(previewDocId !== null) ? 'flex-lg-row-fluid me-lg-4' : 'w-100'} style={(previewDocId !== null) ? {maxWidth: '50%'} : undefined}>
                <label className="form-label required">{lang.labels.selectDocumentToAttach}</label>
                <div className="d-flex gap-3 mb-4">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={lang.labels.search ?? 'Search...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <select
                    className="form-select form-select-sm w-auto"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'new' | 'assigned' | 'all')}
                  >
                    <option value="new">{(lang.labels as any).available ?? 'Available'}</option>
                    <option value="assigned">{lang.labels.assigned}</option>
                    <option value="all">{lang.labels.all}</option>
                  </select>
                </div>
                { loading
                  ?
                  <AppLoading></AppLoading>
                  :
                  filteredDocuments.length === 0
                  ?
                  <div className="text-center text-muted py-5">{lang.labels.noDocumentsAvailable}</div>
                  :
                  <div className="mh-300px scroll-y">
                    { filteredDocuments.map((doc) => {
                      const docId = doc.id ?? (doc as any)._id
                      return (
                        <div
                          key={docId}
                          className={`d-flex align-items-center p-3 rounded cursor-pointer mb-2 ${selectedDocId === docId ? 'bg-light-primary border border-primary' : 'bg-light'}`}
                          onClick={() => handlePreview(docId)}
                        >
                          <span className="svg-icon svg-icon-2 me-3">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black" />
                              <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                            </svg>
                          </span>
                          <div className="d-flex flex-column flex-grow-1">
                            <span className="fw-bold text-dark">{doc.name ?? doc.original_filename}</span>
                            <span className="text-muted fs-7">{doc.dated_on ? dateFormat(lang.code, doc.dated_on) : dateFormat(lang.code, doc.uploaded_at)}</span>
                          </div>
                          { selectedDocId === docId &&
                            <span className="svg-icon svg-icon-2 svg-icon-primary">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.02396 16.4147C9.54916 16.9768 10.4961 16.9768 11.0213 16.4147L18.1427 9.07357C18.6244 8.56148 18.6244 7.76296 18.1427 7.25087C17.6175 6.69259 16.7303 6.69259 16.2051 7.25087L9.89557 13.4982Z" fill="black"/>
                              </svg>
                            </span>
                          }
                        </div>
                      )
                    })}
                  </div>
                }
              </div>
              { (previewDocId !== null) &&
                <div className="flex-lg-row-auto" style={{width: '50%', minHeight: '70vh'}}>
                  <div className="card h-100">
                    <div className="card-header py-2 d-flex justify-content-between align-items-center">
                      <h6 className="m-0">{lang.labels.preview ?? 'Preview'}</h6>
                      <button className="btn btn-sm btn-icon" type="button" onClick={closePreview}>
                        <i className="bi bi-x fs-4"></i>
                      </button>
                    </div>
                    <div className="card-body p-0" style={{height: '70vh'}}>
                      { previewLoading
                        ? <div className="d-flex justify-content-center align-items-center h-100"><span className="spinner-border text-primary"></span></div>
                        : previewUrl
                          ? <AppPdfViewer pdf={previewUrl} />
                          : <div className="d-flex justify-content-center align-items-center h-100 text-muted">{lang.labels.noPreviewAvailable ?? 'No preview available'}</div>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-light me-3" type="button" data-bs-dismiss="modal">{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" data-kt-indicator={loadIndicator} disabled={!selectedDocId} onClick={submitAttach}>
              <span className="indicator-label">{lang.labels.attach}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalAttachDocument;
